import { BizEntityTypeCategory } from "@optima-stride-ai/core";
import { URLTemplates } from '../shared/enums/app-constants';
import { IOptimaHTMLElementProp, OptimaHTMLElement, OptimaHTMLPropNames, OptimaNativeHTMLElementTypes } from "@optima-stride-ai/optima-components";
import { BooleanString } from '../../enum/query-params';

/**
 * Build a query string from multiple possible parameter inputs.
 *
 * - Array values are appended as repeated keys (e.g. id=1&id=2).
 * - Dates are normalized to ISO where possible.
 * - Booleans are appended as `true`/`false` when provided.
 *
 * @param ids - array of biz entity ids -> appended as `id`
 * @param startDateTime - optional start datetime (string). If parseable, converted to ISO.
 * @param endDateTime - optional end datetime (string). If parseable, converted to ISO.
 * @param additionalParams - arbitrary extra params (string or string[]). Empty values ignored.
 * @param parentId - appended as `parentBizEntityId`
 * @param getAncestorsForBizEntityId - appended as `childBizEntityId`
 * @param typeIds - appended as `typeId`
 * @param categoryTypes - appended as `categoryType` (enum/string array)
 * @param onlyDraft - appended as `onlyDraft=true|false` if provided
 * @param includeDraft - appended as `includeDraft=true|false` if provided
 */
/**
 * @deprecated all these methos should move to library
 */
export function convertIdArrayToQueryString(
  ids?: string[],
  startDateTime?: string,
  endDateTime?: string,
  additionalParams?: Record<string, string | string[]>,
  parentId?: string,
  getAncestorsForBizEntityId?: string,
  typeIds?:string[],
  categoryTypes?:BizEntityTypeCategory[],
  onlyDraft?: boolean,
  includeDraft?: boolean
): string {
  const params = new URLSearchParams();

  // Add IDs only if provided and non-empty
  if (ids?.length) {
    ids.forEach(id => {
      if (id) params.append('id', id);
    });
  }

  // Helper to format date-time strings to ISO 8601
  const formatDateTime = (dt?: string): string | undefined => {
    return dt?.includes(' ') ? dt.replace(' ', 'T') : dt;
  };

  // Add optional date-time params after formatting
  const formattedStart = formatDateTime(startDateTime);
  const formattedEnd = formatDateTime(endDateTime);

  if (formattedStart) {
    params.append('startDateTime', formattedStart);
  }

  if (formattedEnd) {
    params.append('endDateTime', formattedEnd);
  }

  // Add additional key-value or key-arrayValue pairs
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => v && params.append(key, v));
      } else if (value) {
        params.append(key, value);
      }
    });
  }
  
  // Add parentId if provided
  if (parentId) {
    params.append('parentBizEntityId', parentId);
  }
  
  // Add getAncestorsForBizEntityId if provided
  if (getAncestorsForBizEntityId) {
    params.append('childBizEntityId', getAncestorsForBizEntityId);
  }

  if(typeIds?.length) {
    typeIds.forEach(typeId => {
      if (typeId) params.append('bizEntityTypeIds', typeId);
    });
  }
  
  if(categoryTypes?.length) {
    categoryTypes.forEach(categoryType => {
      if (categoryType) params.append('bizEntityTypeCategories', categoryType.toString());
    }); 
  }

  if (onlyDraft !== undefined) {
    params.append('onlyDraft', onlyDraft.toString());
  }
  
  if (includeDraft !== undefined) {
    params.append('includeDraft', includeDraft.toString());
  }
  
  return params.toString();
}
/**
 * @deprecated all these methos should move to library
 */
export function getAvatarUrlFromEmail(email:string) {
    const nameFromEmail = email.split('@')[0].replace(/[._]/g, ' '); // e.g., john_doe -> John Doe
    const formattedName = nameFromEmail
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  
    return URLTemplates.UI_AVATARS_API
      .replace('{name}', encodeURIComponent(formattedName))
      .replace('{background}', '0D8ABC')
      .replace('{color}', 'fff');
}

export function getAvatarEle(): OptimaHTMLElement {
    let props: IOptimaHTMLElementProp[] = [];
    props.push({ key: OptimaHTMLPropNames.class, value: 'avatar-images' });
    let ele = new OptimaHTMLElement(OptimaNativeHTMLElementTypes.img, props);
    return ele;
}

/**
 * Creates an OptimaHTMLElement for the SVG thumbnail component
 * @param entityData The entity data to pass to the component

 */
export function getFileThumbnailSvgEle(entityData: any): OptimaHTMLElement {
    let props: IOptimaHTMLElementProp[] = [];
    props.push({ key: OptimaHTMLPropNames.class, value: 'file-thumbnail-svg' });
    props.push({ key: 'entity' as unknown as OptimaHTMLPropNames, value: JSON.stringify(entityData) });
    props.push({ key: 'width' as unknown as OptimaHTMLPropNames, value: '80' });
    props.push({ key: 'height' as unknown as OptimaHTMLPropNames, value: '70' });
    props.push({ key: 'showExtension' as unknown as OptimaHTMLPropNames, value: BooleanString.TRUE });
    
    // Use a custom component type
    let ele = new OptimaHTMLElement('app-file-thumbnail-svg' as unknown as OptimaNativeHTMLElementTypes, props);
    return ele;
}